import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Badge, notification, Select } from 'antd';
import VAPayerComparisonDetails from './VAPayerComparisonDetails';
import VAPayerComparisonLoader from './VAPayerComparisonLoader';
import {
	XThemeVoiceAssTableCard,
	XThemeVSGenerateAL,
	XVSLottie,
} from '../../VoiceAssistanceStyle';
import InitialBatchLoader from '../../../../json/initial_animation_batch.json';
import { XChatTagSpider } from '../../../ShieldMaxx/ShieldMaxxStyle';
// Add new imports
import { getInsuranceDropdownApi } from '../../../../actions/getInsuranceDropdownAction';
import {
	getInsuranceCompaniesBucketApi,
	getInsuranceCompaniesBucketReset,
} from '../../../../actions/getInsuranceCompaniesBucketAction';

const VAPayerComparisonController = (props) => {
	const {
		action,
		actionParameters,
		onActionReceived,
		setProcessingAction,
		onUserInputRequired,
		recentMessages,
		// Add new props for insurance dropdown and bucket APIs
		getInsuranceDropdown,
		getInsuranceDropdownState,
		getInsuranceCompaniesBucket,
		getInsuranceCompaniesBucketState,
		getInsuranceCompaniesBucketReset,
	} = props;

	// List of colors for payers
	const PAYER_COLOR_LIST = [
		'#4A90E2', // Blue
		'#50C878', // Emerald Green
		'#FF6B6B', // Coral Red
		'#FFB366', // Orange
		'#9B59B6', // Purple
		'#3498DB', // Light Blue
		'#E74C3C', // Red
		'#2ECC71', // Green
		'#F1C40F', // Yellow
		'#1ABC9C', // Turquoise
		'#E67E22', // Dark Orange
		'#8E44AD', // Dark Purple
		'#16A085', // Dark Turquoise
		'#D35400', // Burnt Orange
		'#27AE60', // Dark Green
		'#2980B9', // Dark Blue
		'#E84393', // Pink
		'#6C5CE7', // Soft Purple
		'#00CEC9', // Robin's Egg Blue
		'#FD79A8', // Light Pink
	];

	// Assign colors to payers
	const assignPayerColors = (payerName) => {
		// Create a simple hash of the payer name to ensure consistent color assignment
		const hash = payerName.split('').reduce((acc, char) => {
			return char.charCodeAt(0) + ((acc << 5) - acc);
		}, 0);

		// Use the hash to pick a color from the list
		const index = Math.abs(hash) % PAYER_COLOR_LIST.length;
		return PAYER_COLOR_LIST[index];
	};

	// Helper function to process insurance company data
	const processInsuranceData = (company) => ({
		payerName: company.insuranceCompanyName,
		payerId: company.insuranceCompany,
		color: assignPayerColors(company.insuranceCompanyName),
		metrics: {
			performance: {
				label: 'Performance',
				data: [
					parseFloat(company.timely_processing) || 0,
					parseFloat(company.timely_paid) || 0,
					parseFloat(company.overturnedPercentage) || 0,
					parseFloat(company.rejectionPercentage) || 0,
					parseFloat(company.overallScore) || 0,
					parseFloat(company.upheldCountPercentage) || 0,
				],
				labels: [
					'Timely Processing',
					'Timely Paid',
					'Overturned Rate',
					'Rejection Rate',
					'Overall Score',
					'Upheld Rate',
				],
				labelsWithValues: [
					{
						label: 'Timely Processing',
						value1: 85,
						value2: 92,
						value3: 10,
					},
					{
						label: 'Timely Paid',
						value1: 90,
						value2: 74,
						value3: '90',
					},
					{
						label: 'Overturned Rate',
						value1: 20,
						value2: 9.23,
						value3: 10,
					},
					{
						label: 'Rejection Rate',
						value1: 34.07,
						value2: 18.06,
						value3: 10,
					},
					{
						label: 'Overall Score',
						value1: 60.3,
						value2: 62.6,
						value3: 10,
					},
					{
						label: 'Upheld Rate',
						value1: 63,
						value2: 32,
						value3: 10,
					},
				],
			},
		},
		summary: {
			preService: {
				count:
					company.revenueCycleCount.pre_service_count +
					company.revenueCycleCount.pre_service_part_c_count,
				amount: formatAmount(
					company.revenueCycleAmount.pre_service_amount +
						company.revenueCycleAmount.pre_service_part_c_amount,
				),
			},
			postClaim: {
				count:
					company.revenueCycleCount.post_claim_count +
					company.revenueCycleCount.post_claim_part_c_count,
				amount: formatAmount(
					company.revenueCycleAmount.post_claim_amount +
						company.revenueCycleAmount.post_claim_part_c_amount,
				),
			},
			partBDrugs: {
				count: company.revenueCycleCount.part_b_drugs_count,
				amount: formatAmount(
					company.revenueCycleAmount.part_b_drugs_amount,
				),
			},
		},
	});

	// Helper function to format amount similar to Dashboard.js
	const formatAmount = (amount) => {
		if (!amount) return '0';
		if (amount >= 1000000) {
			return (amount / 1000000).toFixed(1) + 'M';
		} else if (amount >= 1000) {
			return (amount / 1000).toFixed(1) + 'K';
		}
		return amount.toString();
	};

	// Demo data for insurance companies
	const DEMO_INSURANCE_COMPANIES = [
		{
			insuranceCompany: { $oid: '6577ec732436c903fe3f9adf' },
			insuranceCompanyName: 'CGS Administrators, LLC',
			insuranceCompanyLogo: {
				uid: 'rc-upload-1716534743715-21',
				name: 'cgs.png',
				status: 'done',
				url: '/insurance_companies/rc-upload-1716534743715-21/CGS Administrators, LLC.png',
				_id: { $oid: '665040118ae29ce845068e32' },
			},
			timely_processing: '92.31',
			revenueCycleCount: {
				pre_service_count: 143,
				post_claim_count: 0,
				part_b_drugs_count: 0,
				pre_service_part_c_count: 0,
				post_claim_part_c_count: 0,
			},
			revenueCycleAmount: {
				pre_service_amount: 614435,
				post_claim_amount: 0,
				part_b_drugs_amount: 0,
				pre_service_part_c_amount: 0,
				post_claim_part_c_amount: 0,
			},
			overallScore: '78.5',
			upheldCountPercentage: '14.6',
			overturnedPercentage: '21.2',
			initialRejectionPercentage: '0.70',
			rejectionPercentage: '20.43',
			timely_paid: '78.6',
		},
		{
			insuranceCompany: { $oid: '6577ec732436c903fe3f9ae1' },
			insuranceCompanyName: 'Noridian Healthcare Solutions, LLC',
			insuranceCompanyLogo: {
				uid: 'rc-upload-1716534743715-25',
				name: 'nordian.jpg',
				status: 'done',
				url: '/insurance_companies/rc-upload-1716534743715-25/Noridian Healthcare Solutions, LLC.jpg',
				_id: { $oid: '665040be8ae29ce84506e9c2' },
			},
			timely_processing: '88.20',
			revenueCycleCount: {
				pre_service_count: 1676,
				post_claim_count: 4,
				part_b_drugs_count: 2,
				pre_service_part_c_count: 0,
				post_claim_part_c_count: 0,
			},
			revenueCycleAmount: {
				pre_service_amount: 13219609,
				post_claim_amount: 81183,
				part_b_drugs_amount: 10000,
				pre_service_part_c_amount: 0,
				post_claim_part_c_amount: 0,
			},
			overallScore: '82.8',
			upheldCountPercentage: '48.6',
			overturnedPercentage: '31.2',
			initialRejectionPercentage: '0',
			rejectionPercentage: '41.79',
			timely_paid: '90.2',
		},
		{
			insuranceCompany: { $oid: '6577ec732436c903fe3f9ae2' },
			insuranceCompanyName: 'National Government Services, Inc.',
			insuranceCompanyLogo: {
				uid: 'rc-upload-1716534743715-29',
				name: 'national gov.jpeg',
				status: 'done',
				url: '/insurance_companies/rc-upload-1716534743715-29/National Government Services, Inc..jpeg',
				_id: { $oid: '665041078ae29ce84507084e' },
			},
			timely_processing: '64.63',
			revenueCycleCount: {
				pre_service_count: 50,
				post_claim_count: 31,
				part_b_drugs_count: 1,
				pre_service_part_c_count: 0,
				post_claim_part_c_count: 0,
			},
			revenueCycleAmount: {
				pre_service_amount: 3649393,
				post_claim_amount: 190722,
				part_b_drugs_amount: 1000,
				pre_service_part_c_amount: 0,
				post_claim_part_c_amount: 0,
			},
			overallScore: '48.7',
			upheldCountPercentage: '21.2',
			overturnedPercentage: '55.4',
			initialRejectionPercentage: '62.2',
			rejectionPercentage: '24.63',
			timely_paid: '41.3',
		},
		{
			insuranceCompany: { $oid: '6577ec732436c903fe3f9ae3' },
			insuranceCompanyName: 'Novitas Solutions, Inc.',
			insuranceCompanyLogo: {
				uid: 'rc-upload-1716534743715-9',
				name: 'novitas_solutions_inc_logo.jpeg',
				status: 'done',
				url: '/insurance_companies/rc-upload-1716534743715-9/Novitas Solutions, Inc..jpeg',
				_id: { $oid: '66503ff08ae29ce845068e1a' },
			},
			timely_processing: '51.35',
			revenueCycleCount: {
				pre_service_count: 9,
				post_claim_count: 26,
				part_b_drugs_count: 2,
				pre_service_part_c_count: 0,
				post_claim_part_c_count: 0,
			},
			revenueCycleAmount: {
				pre_service_amount: 63500,
				post_claim_amount: 130001,
				part_b_drugs_amount: 10000,
				pre_service_part_c_amount: 0,
				post_claim_part_c_amount: 0,
			},
			overallScore: '60.3',
			upheldCountPercentage: '21.3',
			overturnedPercentage: '18.4',
			initialRejectionPercentage: '27.6',
			rejectionPercentage: '34.07',
			timely_paid: '66.1',
		},
		{
			insuranceCompany: { $oid: '6577ec732436c903fe3f9ae6' },
			insuranceCompanyName: 'United HealthCare',
			insuranceCompanyLogo: {
				uid: 'rc-upload-1718004884105-2',
				name: 'Component 1.png',
				status: 'done',
				url: '/insurance_companies/rc-upload-1718004884105-2/United HealthCare.webp',
				_id: { $oid: '673854a3bf56299f52fb8bd4' },
			},
			timely_processing: '70.97',
			revenueCycleCount: {
				pre_service_count: 29,
				post_claim_count: 0,
				part_b_drugs_count: 1,
				pre_service_part_c_count: 0,
				post_claim_part_c_count: 0,
			},
			revenueCycleAmount: {
				pre_service_amount: 463249,
				post_claim_amount: 0,
				part_b_drugs_amount: 8569,
				pre_service_part_c_amount: 0,
				post_claim_part_c_amount: 0,
			},
			overallScore: '62.6',
			upheldCountPercentage: '20',
			overturnedPercentage: '32.3',
			initialRejectionPercentage: '32.3',
			rejectionPercentage: '18.06',
			timely_paid: '78.66',
		},
	];

	const [currentView, setCurrentView] = useState('default');
	const [isLoading, setIsLoading] = useState(false);
	// State management - initialize with first n payers
	const [selectedPayers, setSelectedPayers] = useState(() => {
		const initialSelections = {};
		DEMO_INSURANCE_COMPANIES.forEach((payer, index) => {
			initialSelections[`payer${index + 1}`] = payer.insuranceCompanyName;
		});
		return initialSelections;
	});
	const [payers, setPayers] = useState([]);
	const [useDemoData, setUseDemoData] = useState(false);
	const [dynamicPayerData, setDynamicPayerData] = useState([]);
	// Add new state for add payer functionality
	const [insuranceDropdownOptions, setInsuranceDropdownOptions] = useState(
		[],
	);

	// Add state for dropdown value
	const [dropdownValue, setDropdownValue] = useState(null);
	// Add state for bucket API loading
	const [isBucketLoading, setIsBucketLoading] = useState(false);

	// Effect to handle action updates
	useEffect(() => {
		console.log('Action received in controller:', action, actionParameters);

		if (action === 'add_payer' && actionParameters) {
			handleAddPayer(actionParameters);
		}
		if (action === 'update_payer' && actionParameters) {
			handleUpdatePayer(actionParameters);
		}
		if (
			action === 'payer_score_card_filter' &&
			actionParameters?.insuranceCompanies
		) {
			console.log(
				'Processing payer score card filter with data:',
				actionParameters.insuranceCompanies,
			);

			// Reset dropdown state when data overrides
			resetDropdownState();

			// Check if we have real data, if not use demo data
			const hasRealData =
				actionParameters.insuranceCompanies &&
				Array.isArray(actionParameters.insuranceCompanies) &&
				actionParameters.insuranceCompanies.length > 0;

			if (!hasRealData) {
				console.log('No real data available, using demo data');
				setUseDemoData(true);
			}

			const processedData =
				useDemoData || !hasRealData
					? DEMO_INSURANCE_COMPANIES.map((company) =>
							processInsuranceData(company),
					  )
					: actionParameters.insuranceCompanies.map((company) =>
							processInsuranceData(company),
					  );

			setDynamicPayerData(processedData);

			// Initialize selected payers with all companies
			if (processedData.length > 0) {
				const initialSelections = {};
				processedData.forEach((company, index) => {
					initialSelections[`payer${index + 1}`] = company.payerName;
				});
				setSelectedPayers(initialSelections);
			}

			// Set view to details and stop loading
			setCurrentView('details');
			setIsLoading(false);
			setProcessingAction(false);
		}

		// Handle any other payer-related actions
		if (action && action.includes('payer')) {
			setCurrentView('details');
			setIsLoading(false);
		}
	}, [action, actionParameters, useDemoData]);

	// Effect to sync with chat messages
	useEffect(() => {
		if (recentMessages?.length > 0) {
			const lastMessage = recentMessages[recentMessages.length - 1];
			// Check if the message contains payer-related updates
			if (lastMessage.content?.toLowerCase().includes('payer')) {
				checkForPayerUpdates(lastMessage);
			}
		}
	}, [recentMessages]);

	const checkForPayerUpdates = (message) => {
		// Parse message content for payer updates
		// This is a simplified example - expand based on your chat message structure
		if (
			message.type === 'user' &&
			message.content.toLowerCase().includes('add payer')
		) {
			setIsLoading(true);
			// Notify chat that we're processing
			onUserInputRequired({
				type: 'payer_update',
				message: 'Processing payer addition...',
			});
		}
	};

	const handleAddPayer = async (params) => {
		try {
			setIsLoading(true);
			const { payerId, payerName, payerColor } = params;

			// Add new payer to state
			const newPayer = {
				id: payerId,
				name: payerName,
				color: payerColor || 'blue', // default color
			};

			setPayers((prev) => [...prev, newPayer]);

			// Notify chat of successful addition
			onActionReceived('payer_added', {
				payerId,
				payerName,
				success: true,
			});
		} catch (error) {
			console.error('Error adding payer:', error);
			onActionReceived('payer_added', {
				error: true,
				message: 'Failed to add payer',
			});
		} finally {
			setIsLoading(false);
			setProcessingAction(false);
		}
	};

	const handleUpdatePayer = (params) => {
		const { payerId, updates } = params;
		setPayers((prev) =>
			prev.map((payer) =>
				payer.id === payerId ? { ...payer, ...updates } : payer,
			),
		);
	};

	// Handlers
	const handlePayerChange = (value, payerPosition) => {
		setSelectedPayers((prev) => ({
			...prev,
			[payerPosition]: value,
		}));
		// Notify chat of payer selection
		onActionReceived('payer_selected', {
			payerId: value,
			position: payerPosition,
		});
	};

	// Handle payer removal
	const handlePayerRemove = (payerPosition) => {
		const payerNameToRemove = selectedPayers[payerPosition];

		// Remove from selectedPayers
		setSelectedPayers((prev) => {
			const newPayers = { ...prev };
			delete newPayers[payerPosition];
			return newPayers;
		});

		// Remove from dynamicPayerData
		setDynamicPayerData((prevData) => {
			return prevData.filter(
				(payer) => payer.payerName !== payerNameToRemove,
			);
		});
	};

	// Get the current active dataset
	const getCurrentDataSet = () => {
		if (useDemoData) {
			return DEMO_INSURANCE_COMPANIES.map((company) =>
				processInsuranceData(company),
			);
		}
		return dynamicPayerData.length > 0 ? dynamicPayerData : [];
	};

	// Get current chart data based on selections
	const getCurrentChartData = () => {
		const currentDataSet = getCurrentDataSet();

		if (!currentDataSet || currentDataSet.length === 0) {
			return {
				labels: [],
				datasets: [],
				options: {
					scales: {
						r: {
							angleLines: {
								display: true,
								lineWidth: 1,
								color: 'rgba(0,0,0,0.1)',
							},
							beginAtZero: true,
							min: 0,
							max: 100,
							ticks: {
								stepSize: 20,
								backdropColor: 'transparent',
							},
							grid: {
								circular: false,
								lineWidth: 1,
								color: 'rgba(0,0,0,0.1)',
							},
						},
					},
					plugins: {
						legend: {
							display: false,
						},
					},
				},
			};
		}

		const datasets = Object.values(selectedPayers)
			.map((payerName) => {
				const payerData = currentDataSet.find(
					(payer) => payer.payerName === payerName,
				);
				if (!payerData || !payerData.metrics) return null;

				const metricData = payerData.metrics.performance;
				if (!metricData) return null;

				return {
					data: metricData.data.map(
						(value) => parseFloat(value) || 0,
					),
					borderColor: payerData.color,
					backgroundColor: `${payerData.color}1A`,
					borderWidth: 2,
					pointBackgroundColor: payerData.color,
					pointBorderColor: '#fff',
					pointBorderWidth: 2,
					pointRadius: 4,
					pointHoverRadius: 6,
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: payerData.color,
					pointHoverBorderWidth: 2,
					fill: true,
				};
			})
			.filter(Boolean);

		return {
			labels: currentDataSet[0]?.metrics?.performance?.labels || [],
			datasets,
			options: {
				scales: {
					r: {
						angleLines: {
							display: true,
							lineWidth: 1,
							color: 'rgba(0,0,0,0.1)',
						},
						beginAtZero: true,
						min: 0,
						max: 100,
						ticks: {
							stepSize: 20,
							backdropColor: 'transparent',
						},
						grid: {
							circular: false,
							lineWidth: 1,
							color: 'rgba(0,0,0,0.1)',
						},
					},
				},
				plugins: {
					legend: {
						display: false,
					},
				},
			},
		};
	};

	// Status color mapping
	const getStatusColor = (value, type) => {
		const statusMap = {
			payer: payers.reduce(
				(acc, payer) => ({
					...acc,
					[payer.id]: payer.color,
				}),
				{},
			),
			metric: {
				performance: 'green',
				financial: 'blue',
				operational: 'purple',
			},
			timeframe: {
				current: 'green',
				quarter: 'orange',
				year: 'blue',
				ytd: 'purple',
			},
		};
		return statusMap[type]?.[value] || 'default';
	};

	// Color mapping for different insurance companies
	const getPayerColor = (index) => {
		const colors = [
			'#4A90E2', // Blue
			'#50C878', // Green
			'#FF6B6B', // Red
			'#FFB366', // Orange
			'#9B59B6', // Purple
			'#3498DB', // Light Blue
		];
		return colors[index % colors.length];
	};

	// Render spider cards dynamically using current dataset
	const renderSpiderCards = () => {
		const currentDataSet = getCurrentDataSet();
		if (!currentDataSet || currentDataSet.length === 0) return null;

		return Object.values(selectedPayers)
			.map((payerName) => {
				const payerData = currentDataSet.find(
					(payer) => payer.payerName === payerName,
				);
				if (!payerData || !payerData.summary) return null;

				return (
					<div
						className="spiderData"
						key={payerName}
						style={{
							backgroundColor: `${payerData.color}1A`,
							borderColor: payerData.color,
							borderWidth: '1px',
							borderStyle: 'solid',
						}}
					>
						<ul>
							<li>
								<div className="spiderDataTitle">
									<span>
										{payerData.summary.preService?.count ||
											0}
									</span>
									Pre-service
								</div>
								<span>
									${payerData.summary.preService?.amount || 0}
								</span>
							</li>
							<li>
								<div className="spiderDataTitle">
									<span>
										{payerData.summary.postClaim?.count ||
											0}
									</span>
									Post Claim
								</div>
								<span>
									${payerData.summary.postClaim?.amount || 0}
								</span>
							</li>
							<li>
								<div className="spiderDataTitle">
									<span>
										{payerData.summary.partBDrugs?.count ||
											0}
									</span>
									Part B Drugs
								</div>
								<span>
									${payerData.summary.partBDrugs?.amount || 0}
								</span>
							</li>
						</ul>
					</div>
				);
			})
			.filter(Boolean);
	};

	// Dynamic dropdown options based on current dataset
	const getDropdownOptions = () => {
		const currentDataSet = getCurrentDataSet();
		if (!currentDataSet || currentDataSet.length === 0) {
			return {};
		}

		const payerOptions = currentDataSet.map((payer) => ({
			value: payer.payerName,
			label: (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
					}}
				>
					<Badge color={payer.color} />
					{payer.payerName}
				</div>
			),
		}));

		// Create dropdown options for each payer position
		const dropdowns = {};
		currentDataSet.forEach((_, index) => {
			dropdowns[`payer${index + 1}`] = payerOptions;
		});

		return dropdowns;
	};

	// Render payer dropdowns dynamically
	const renderPayerDropdowns = () => {
		const currentDataSet = getCurrentDataSet();
		return Object.keys(selectedPayers).map((payerKey, index) => {
			const payerData = currentDataSet.find(
				(payer) => payer.payerName === selectedPayers[payerKey],
			);
			return (
				<React.Fragment key={payerKey}>
					{index > 0 && <span className="vsText">VS</span>}
					<div className="dropdownList noWidth">
						<XChatTagSpider
							closable
							onClose={() => handlePayerRemove(payerKey)}
							onChange={(value) =>
								handlePayerChange(value, payerKey)
							}
						>
							<div
								className="payerColor"
								style={{
									backgroundColor:
										payerData?.color ||
										getPayerColor(index),
									width: '12px',
									height: '12px',
									borderRadius: '50%',
									marginRight: '8px',
								}}
							/>
							{selectedPayers[payerKey]}
						</XChatTagSpider>
					</div>
				</React.Fragment>
			);
		});
	};

	// Add new function to handle insurance dropdown focus
	const handleInsuranceDropdownFocus = () => {
		getInsuranceDropdown();
	};

	// Add new function to handle insurance selection
	const handleInsuranceSelection = async (insuranceId) => {
		if (!insuranceId) return;

		setIsLoading(true);
		setIsBucketLoading(true); // Set bucket loading to true

		try {
			// Get the selected insurance company details
			const selectedInsurance = insuranceDropdownOptions.find(
				(option) => option.value === insuranceId,
			);

			if (!selectedInsurance) {
				notification.error({
					message: 'Error',
					description: 'Selected insurance company not found.',
				});
				return;
			}

			// Check if this payer is already added
			const isAlreadyAdded = dynamicPayerData.some(
				(payer) => payer.payerId === insuranceId,
			);

			if (isAlreadyAdded) {
				// notification.warning({
				// 	message: 'Payer Already Added',
				// 	description: `${selectedInsurance.label} is already in the comparison.`,
				// });
				return;
			}

			// Prepare API parameters
			const apiParams = {
				insuranceCompany: insuranceId,
			};

			// Check if we have date filters from recent messages
			if (recentMessages && recentMessages.length > 0) {
				const lastMessage = recentMessages[recentMessages.length - 1];
				if (lastMessage.parameters) {
					if (
						lastMessage.parameters.date_from &&
						lastMessage.parameters.date_to
					) {
						apiParams.created_on = 'custom';
						apiParams.from = lastMessage.parameters.date_from;
						apiParams.to = lastMessage.parameters.date_to;
					}
				}
			}

			// Make the API call to get payer data
			getInsuranceCompaniesBucket(apiParams);

			// Clear the dropdown immediately after selection
			setDropdownValue(null);
		} catch (error) {
			console.error('Error adding payer:', error);
			// notification.error({
			// 	message: 'Error',
			// 	description: 'Failed to add payer. Please try again.',
			// });
		} finally {
			getInsuranceCompaniesBucketReset();
			setIsLoading(false);
			// Note: isBucketLoading will be set to false in the useEffect when API completes
		}
	};

	// Add useEffect to handle insurance dropdown response
	useEffect(() => {
		if (
			getInsuranceDropdownState.apiState === 'success' &&
			getInsuranceDropdownState.data
		) {
			// Get all available insurance companies
			const allOptions = getInsuranceDropdownState.data.map((item) => ({
				label: item.name,
				value: item._id,
			}));

			// Filter out already added payers
			const filteredOptions = allOptions.filter((option) => {
				// Check if this payer is already in the dynamicPayerData
				const isAlreadyAdded = dynamicPayerData.some(
					(payer) => payer.payerId === option.value,
				);
				return !isAlreadyAdded;
			});

			setInsuranceDropdownOptions(filteredOptions);
		}
	}, [getInsuranceDropdownState, dynamicPayerData]); // Add dynamicPayerData as dependency

	// Helper function to get the next available payer key
	const getNextAvailablePayerKey = () => {
		const existingKeys = Object.keys(selectedPayers);
		if (existingKeys.length === 0) return 'payer1';

		// Find the highest number and add 1
		const numbers = existingKeys.map((key) =>
			parseInt(key.replace('payer', '')),
		);
		const maxNumber = Math.max(...numbers);
		return `payer${maxNumber + 1}`;
	};

	// Add useEffect to handle insurance bucket API response
	useEffect(() => {
		if (
			getInsuranceCompaniesBucketState.apiState === 'success' &&
			getInsuranceCompaniesBucketState.data
		) {
			const newPayerData = getInsuranceCompaniesBucketState.data;

			if (
				newPayerData.insuranceCompanies &&
				newPayerData.insuranceCompanies.length > 0
			) {
				// Process the new payer data
				const processedNewPayer = processInsuranceData(
					newPayerData.insuranceCompanies[0],
				);

				// Append to existing data
				setDynamicPayerData((prevData) => {
					const updatedData = [...prevData, processedNewPayer];

					// Update selected payers to include the new one
					const newPayerKey = getNextAvailablePayerKey(); // Use the helper function
					setSelectedPayers((prevSelected) => ({
						...prevSelected,
						[newPayerKey]: processedNewPayer.payerName,
					}));

					return updatedData;
				});

				// notification.success({
				// 	message: 'Payer Added',
				// 	description: `${processedNewPayer.payerName} has been added to the comparison.`,
				// });
			} else if (newPayerData.insuranceCompanies.length === 0) {
				notification.warning({
					message: 'No payer data found.',
					description: 'Please select a different insurance company.',
				});
			}
			getInsuranceCompaniesBucketReset();
			setIsBucketLoading(false); // Stop bucket loading
		} else if (getInsuranceCompaniesBucketState.apiState === 'error') {
			// notification.error({
			// 	message: 'Error',
			// 	description: 'Failed to fetch payer data. Please try again.',
			// });
			getInsuranceCompaniesBucketReset();
			setIsBucketLoading(false); // Stop bucket loading on error
		}
	}, [getInsuranceCompaniesBucketState]);

	// Add a function to reset dropdown when data overrides
	const resetDropdownState = () => {
		setInsuranceDropdownOptions([]);
	};

	const renderComponent = () => {
		console.log(
			'Rendering component with currentView:',
			currentView,
			'isLoading:',
			isLoading,
		);

		if (isLoading || currentView === 'loading') {
			return <VAPayerComparisonLoader />;
		}

		switch (currentView) {
			case 'details':
				return (
					<VAPayerComparisonDetails
						renderPayerDropdowns={renderPayerDropdowns}
						renderSpiderCards={renderSpiderCards}
						getCurrentChartData={getCurrentChartData}
						// Add new props for add payer functionality
						insuranceDropdownOptions={insuranceDropdownOptions}
						onInsuranceDropdownFocus={handleInsuranceDropdownFocus}
						onInsuranceSelection={handleInsuranceSelection}
						isLoading={isLoading}
						// Add dropdown value props
						dropdownValue={dropdownValue}
						onDropdownChange={setDropdownValue}
						// Add bucket loading prop
						isBucketLoading={isBucketLoading}
					/>
				);
			default:
				return (
					<XThemeVSGenerateAL className="h-100">
						<XVSLottie
							className="relativePositions"
							animationData={InitialBatchLoader}
							loop={true}
						/>
					</XThemeVSGenerateAL>
				);
		}
	};

	return <>{renderComponent()}</>;
};

const mapStateToProps = (state) => ({
	// Add new state mappings
	getInsuranceDropdownState: state.getInsuranceDropdown,
	getInsuranceCompaniesBucketState: state.getInsuranceCompaniesBucket,
});

const mapDispatchToProps = (dispatch) => ({
	// Add new dispatch mappings
	getInsuranceDropdown: (params) => dispatch(getInsuranceDropdownApi(params)),
	getInsuranceCompaniesBucket: (data) =>
		dispatch(getInsuranceCompaniesBucketApi(data)),
	getInsuranceCompaniesBucketReset: (data) =>
		dispatch(getInsuranceCompaniesBucketReset(data)),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(VAPayerComparisonController);
